import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PriceDisplay } from 'dibs-buyer-price';
import { useIntl } from 'dibs-react-intl';
import { isDefaultLang } from 'dibs-intl/exports/languages';

import { PdpSharedFramingStatusLazy } from '../PdpSharedFramingStatus/PdpSharedFramingStatusLazy.jsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PriceDisplayBot } from './PriceDisplayBot.tsx';

import { type PdpSharedPriceDisplay_item$key } from './__generated__/PdpSharedPriceDisplay_item.graphql.ts';

const itemFragment = graphql`
    fragment PdpSharedPriceDisplay_item on Item {
        ...PdpSharedFramingStatus_item @include(if: $afterDisplayOrBtSsr)
        ...PriceDisplayBot_item @include(if: $afterDisplayOrBtSsr)
        skus {
            serviceId
            ...PriceDisplay_sku
                @include(if: $isMultiSku)
                @arguments(
                    hasSkuId: true
                    userId: $userId
                    isTrade: $isTrade
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                    catchNonFatalErrors: false
                )
        }
        ...PdpSharedEstimateValueBadge_item @include(if: $afterDisplayOrBtSsr)
        ...PriceDisplay_item
            @include(if: $afterDisplayOrBtSsr)
            @arguments(
                userId: $userId
                isTrade: $isTrade
                priceBookName: $priceBookName
                userCountryCode: $userCountryCode
                catchNonFatalErrors: false
            )
    }
`;

type Props = {
    item: PdpSharedPriceDisplay_item$key;
    currency: string;
    selectedSkuId: string | null;
};

export const PdpSharedPriceDisplay: FC<Props> = ({ item: itemRef, currency, selectedSkuId }) => {
    const intl = useIntl();
    const item = useFragment(itemFragment, itemRef);
    const { isSsrOnlyBot } = useServerVarsContext();
    const selectedSku = item?.skus?.find(sku => sku?.serviceId === selectedSkuId) || null;
    const showBotPrice = isSsrOnlyBot && isDefaultLang(intl.locale);

    if (showBotPrice) {
        return <PriceDisplayBot item={item} selectedSkuId={selectedSkuId} />;
    }

    return (
        <PriceDisplay
            item={item}
            useSvgIcon
            displayVatLabel
            sku={selectedSku}
            currency={currency}
            transaction={null}
            appendElementToPrice={<PdpSharedFramingStatusLazy item={item} />}
        />
    );
};
